// src/pages/Invoices/utils/zipInvoices.ts
import JSZip from 'jszip';
import { InvoiceFile, Category } from '../types/invoices';
import { saveAs } from 'file-saver';
import { getStorage, ref as storageRef, getBytes } from 'firebase/storage';

type Scope =
  | { level: 'root' }
  | { level: 'category'; category: Category }
  | { level: 'year'; category: Category; year: string }
  | { level: 'month'; category: Category; year: string; month: string };

const VROOT = 'invoices'; // nom du dossier racine dans le zip

// Petite limitation de concurrence pour ne pas saturer le navigateur
async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, i: number) => Promise<R>
): Promise<R[]> {
  const ret: R[] = [];
  let i = 0;
  const run = async () => {
    while (i < items.length) {
      const idx = i++;
      ret[idx] = await fn(items[idx], idx);
    }
  };
  const runners = Array.from({ length: Math.min(limit, items.length) }, run);
  await Promise.all(runners);
  return ret;
}

export function filterFilesByScope(
  all: InvoiceFile[],
  scope: Scope
): InvoiceFile[] {
  if (scope.level === 'root') return all;
  if (scope.level === 'category')
    return all.filter((f) => f.category === scope.category);
  if (scope.level === 'year')
    return all.filter(
      (f) => f.category === scope.category && f.year === scope.year
    );
  // month
  return all.filter(
    (f) =>
      f.category === scope.category &&
      f.year === scope.year &&
      f.month === scope.month
  );
}

export function buildVirtualPath(f: InvoiceFile): string {
  const year = f.year ?? 'unknown';
  const month = f.month ?? 'unknown';
  const category = f.category; // 'gifts' | 'workshops'
  return `${VROOT}/${category}/${year}/${month}/${f.name}`;
}

// --- Helpers Firebase Storage URL ---
function isFirebaseStorageUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      u.hostname.includes('firebasestorage.googleapis.com') &&
      u.pathname.includes('/o/')
    );
  } catch {
    return false;
  }
}

/**
 * Extrait le chemin objet (non encodé) à partir d'une URL de téléchargement
 * Firebase Storage de type:
 * https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<objectPath>?alt=media&token=...
 */
function extractObjectPath(url: string): string | null {
  try {
    const u = new URL(url);
    const idx = u.pathname.indexOf('/o/');
    if (idx === -1) return null;
    const enc = u.pathname.slice(idx + 3); // après "/o/"
    const qmark = enc.indexOf('?');
    const encPath = qmark >= 0 ? enc.slice(0, qmark) : enc;
    return decodeURIComponent(encPath);
  } catch {
    return null;
  }
}

export async function downloadInvoicesZip(all: InvoiceFile[], scope: Scope) {
  const zip = new JSZip();

  const scoped = filterFilesByScope(all, scope);
  if (scoped.length === 0)
    throw new Error('Aucun fichier à télécharger pour ce dossier.');

  // Téléchargement en parallèle avec limite (5)
  await mapLimit(scoped, 5, async (f) => {
    const url = f.url;
    if (!url) throw new Error(`URL manquante pour ${f.name}`);

    let arrayBuffer: ArrayBuffer | null = null;

    // 1) tentative via fetch standard
    try {
      const res = await fetch(url, {
        cache: 'no-store',
        mode: 'cors',
        credentials: 'omit',
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      arrayBuffer = await res.arrayBuffer();
    } catch (err: any) {
      const msg = String(err?.message || err);

      // 2) fallback si 403 sur Firebase Storage → getBytes() (auth Firebase)
      const is403 = msg.includes('403') || msg.includes('HTTP 403');
      if (is403 && isFirebaseStorageUrl(url)) {
        const objectPath = extractObjectPath(url);
        if (!objectPath)
          throw new Error(`Échec ${f.name} → 403 et chemin objet introuvable`);
        const storage = getStorage(); // l'app Firebase doit être initialisée en amont
        const sref = storageRef(storage, objectPath);
        // Optionnel: ajouter une limite de taille (ex: 20 Mo) en second paramètre
        arrayBuffer = await getBytes(sref /*, 20 * 1024 * 1024 */);
      } else {
        // autre erreur → on propage avec contexte
        throw new Error(
          `Échec "${f.name}" (${f.category}/${f.year}/${f.month}) → ${msg}`
        );
      }
    }

    const path = buildVirtualPath(f);
    zip.file(path, arrayBuffer!);
  });

  const filename =
    scope.level === 'root'
      ? `${VROOT}.zip`
      : scope.level === 'category'
        ? `${VROOT}-${scope.category}.zip`
        : scope.level === 'year'
          ? `${VROOT}-${scope.category}-${scope.year}.zip`
          : `${VROOT}-${scope.category}-${scope.year}-${scope.month}.zip`;

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, filename);
}
